import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useForm, useWatch, type Resolver } from "react-hook-form";

import { RadioInput, SelectInput, TextInput } from "@/shared/components";
import DateInput from "@/shared/components/DateInput";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { LIFE_STATIC_OPTIONS } from "../../common/static";
import {
  defaultLifePlanDetailsFormValues,
  LifePlanDetailsFormValues,
} from "../../redux/lifeQuoteSlice";
import { lifePlanDetailsSchema } from "../../validation/validation.life";

const option = (label: string, value = label.toLowerCase().replace(/ /g, "_")) => ({
  label,
  value,
});

const formatIndianAmount = (value: string) => {
  const digits = String(value || "").replace(/[^\d]/g, "");

  if (!digits) {
    return "";
  }

  if (digits.length <= 3) {
    return digits;
  }

  const lastThree = digits.slice(-3);
  const remaining = digits.slice(0, -3);
  const grouped = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return `${grouped},${lastThree}`;
};

export const LifePlanDetailsStep = forwardRef<
  StepFormHandle<LifePlanDetailsFormValues>,
  StepFormComponentProps<LifePlanDetailsFormValues, { layoutClassName?: string }>
>(function LifePlanDetailsStep({ defaultValues, layoutClassName }, ref) {
  const today = new Date().toISOString().split("T")[0];
  const form = useForm<LifePlanDetailsFormValues>({
    resolver: zodResolver(lifePlanDetailsSchema) as Resolver<LifePlanDetailsFormValues>,
    defaultValues: defaultValues ?? defaultLifePlanDetailsFormValues,
    mode: "onChange",
  });
  const planOption = useWatch({ control: form.control, name: "planOption" });
  const payoutType = useWatch({ control: form.control, name: "payoutType" });

  useEffect(() => {
    if (payoutType === "lumpsum") {
      form.setValue("payoutTerm", "", { shouldDirty: true, shouldValidate: true });
      form.setValue("lumpsumPercentage", "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (payoutType === "fixed_income") {
      form.setValue("lumpsumPercentage", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [form, payoutType]);

  useEffect(() => {
    form.reset(defaultValues ?? defaultLifePlanDetailsFormValues);
  }, [defaultValues, form]);

  useImperativeHandle(ref, () => ({
    submit: () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  return (
    <div className="space-y-6">
      <div className={layoutClassName ?? "grid grid-cols-1 gap-4 sm:grid-cols-2"}>
        <DateInput
          control={form.control}
          name="lifeAssuredDob"
          label="Life Assured Date of Birth"
          placeholder="Enter Date of Birth"
          max={today}
          yearOrder="desc"
          required
          showAge
        />
        <RadioInput
          control={form.control}
          name="lifeAssuredGender"
          label="Life Assured Gender"
          options={[option("Male", "male"), option("Female", "female")]}
          required
        />

        <RadioInput
          control={form.control}
          name="smoker"
          label="Do you smoke?"
          options={[option("Yes", "yes"), option("No", "no")]}
          required
        />

        <SelectInput
          control={form.control}
          name="planOption"
          label="Select Plan Option"
          options={LIFE_STATIC_OPTIONS.planOption}
          required
        />
        <TextInput
          control={form.control}
          name="sumAssured"
          label="Sum Assured"
          placeholder="Enter Sum Assured Amount"
          inputMode="numeric"
          formatValueOnBlur={formatIndianAmount}
          required
        />
        <SelectInput
          control={form.control}
          name="policyTerm"
          label="Policy Term"
          placeholder="Policy Term"
          options={LIFE_STATIC_OPTIONS.policyTerm}
          required
        />
        <SelectInput
          control={form.control}
          name="premiumPaymentTerm"
          label="Premium Payment Term"
          placeholder="Premium Payment Term"
          options={LIFE_STATIC_OPTIONS.premiumPaymentTerm}
          required
        />
        <SelectInput
          control={form.control}
          name="premiumPaymentFrequency"
          label="Premium Payment Frequency"
          placeholder="Premium Payment Frequency"
          options={LIFE_STATIC_OPTIONS.premiumPaymentFrequency}
          required
        />
        <SelectInput
          control={form.control}
          name="category"
          label="Category"
          placeholder="Category"
          options={LIFE_STATIC_OPTIONS.category}
          required
        />
        <SelectInput
          control={form.control}
          name="payoutType"
          label="Payout Type"
          options={LIFE_STATIC_OPTIONS.payoutType}
          required
        />
        {payoutType === "fixed_income" || payoutType === "mixed" ? (
          <SelectInput
            control={form.control}
            name="payoutTerm"
            label="Payout Term"
            placeholder="Select Payout Term"
            options={LIFE_STATIC_OPTIONS.payoutTerm}
            required
          />
        ) : null}
        {payoutType === "mixed" ? (
          <TextInput
            control={form.control}
            name="lumpsumPercentage"
            label="Lumpsum Percentage"
            placeholder="Enter Lumpsum Percentage"
            inputMode="numeric"
            required
          />
        ) : null}
        {planOption === "extra_life_option" ? (
          <TextInput
            control={form.control}
            name="accidentalDeathSumAssured"
            label="Accidental Death Sum Assured"
            placeholder="Enter accidental death sum assured"
            inputMode="numeric"
            required
          />
        ) : null}
      </div>
    </div>
  );
});
